/**
 * 购买流程：选套餐 → 确认协议 → 建单 → 支付二维码
 * 模板需放置 PlanSelectDialog、PurchaseAgreementDialog、PayQrDialog、PurchaseSuccessDialog
 */
import PlanSelectDialog from './PlanSelectDialog.vue';
import PayQrDialog from './PayQrDialog.vue';
import PurchaseAgreementDialog from './PurchaseAgreementDialog.vue';
import PurchaseSuccessDialog from './PurchaseSuccessDialog.vue';
import { createOrder } from './api';
import { PAY_CHANNEL } from './config';
import {
  signAgreement,
  AGREEMENT_TYPE_FORMAL,
  buildView,
  SERVICE_STATUS,
  isTrialSubscriptionExpired,
} from '../subscriptionService/subscriptionService';
import {
  refreshIndivSession,
  subscriptionState,
} from '../indivSession/indivSessionService';
import { isRealnameVerified } from '../realname/realnameService';

export default {
  components: {
    PlanSelectDialog,
    PayQrDialog,
    PurchaseAgreementDialog,
    PurchaseSuccessDialog,
  },

  data() {
    return {
      showPlanDialog: false,
      showPurchaseAgreement: false,
      showPayQrDialog: false,
      showPurchaseSuccessDialog: false,
      purchasing: false,
      selectedPlan: null,
      payOrderNo: '',
      payAmount: '',
      purchaseTrialTip: false,
      purchaseRenewTip: false,
      purchaseSuccessTrialTip: false,
      purchaseSuccessRenewTip: false,
    };
  },

  methods: {
    /** 试用进行中且首次购买：提示叠加试用剩余天数；试用已到期则走「已开通」文案 */
    isTrialPurchaseTip(subscription) {
      const status = subscription || subscriptionState.status;
      const view = buildView(status);
      return (
        view.purchaseTip &&
        !view.prepaid &&
        !isTrialSubscriptionExpired(status)
      );
    },

    /**
     * 续费文案：正式服务有效期内续费，或试用期内已预购后再买。
     * 试用到期后首次开通、正式到期后首次开通等仍走「已开通」文案。
     */
    isRenewPurchaseTip(subscription) {
      const status = subscription || subscriptionState.status;
      if (!status || !status.hasService) return false;
      const ss = Number(status.serviceStatus);
      if (ss === SERVICE_STATUS.FORMAL) return true;
      if (ss === SERVICE_STATUS.TRIAL && Number(status.pendingYears) > 0) {
        return true;
      }
      return false;
    },

    /** 打开选套餐弹窗 */
    openPurchaseDialog(subscription) {
      this.purchaseTrialTip = this.isTrialPurchaseTip(subscription);
      this.purchaseRenewTip = this.isRenewPurchaseTip(subscription);
      this.showPlanDialog = true;
    },

    /** 已有订单，直接打开支付二维码 */
    openOrderPayDialog(order) {
      if (!order || !order.orderNo) {
        this.$message.warning('订单信息无效');
        return;
      }
      this.payOrderNo = order.orderNo;
      this.payAmount = order.amount || '';
      this.purchaseTrialTip = this.isTrialPurchaseTip();
      this.purchaseRenewTip = this.isRenewPurchaseTip();
      this.showPayQrDialog = true;
    },

    /** 选套餐后进入协议确认 */
    onPlanConfirm(plan) {
      this.selectedPlan = plan;
      this.showPlanDialog = false;
      this.showPurchaseAgreement = true;
    },

    /** 同意协议后签署并创建订单 */
    onPurchaseAgreementAgree() {
      if (this.purchasing || !this.selectedPlan) {
        return;
      }

      this.purchasing = true;
      signAgreement(AGREEMENT_TYPE_FORMAL)
        .then((signResult) => {
          const agreementId =
            signResult && signResult.agreementId != null
              ? signResult.agreementId
              : null;
          if (!agreementId) {
            throw new Error('协议签署失败');
          }
          return createOrder({
            years: this.selectedPlan.years,
            channel: PAY_CHANNEL,
            agreementId,
          });
        })
        .then((order) => {
          if (!order || !order.orderNo) {
            throw new Error('订单创建失败');
          }
          this.showPurchaseAgreement = false;
          this.payOrderNo = order.orderNo;
          this.payAmount = order.amount || this.selectedPlan.price;
          this.showPayQrDialog = true;
          if (typeof this.onOrderCreated === 'function') {
            this.onOrderCreated();
          }
        })
        .catch((err) => {
          this.$message.error((err && err.message) || '操作失败，请稍后重试');
        })
        .finally(() => {
          this.purchasing = false;
        });
    },

    /** 支付成功：刷新会话并展示成功弹窗 */
    onPurchasePaid() {
      refreshIndivSession({ force: true, store: this.$store }).then(
        () => {
          this.purchaseSuccessTrialTip = this.isTrialPurchaseSuccessTip();
          this.purchaseSuccessRenewTip = this.isRenewPurchaseSuccessTip();
          this.showPurchaseSuccessDialog = true;
          if (typeof this.onPurchaseSuccess === 'function') {
            this.onPurchaseSuccess();
          }
        }
      );
    },

    /**
     * 支付成功弹窗是否展示「已叠加试用剩余天数」文案：
     * 仅试用进行中且首次购买（购买前未预购）；试用已到期、已预购后再买、续费等不展示。
     */
    isTrialPurchaseSuccessTip() {
      return this.purchaseTrialTip;
    },

    /** 支付成功弹窗是否展示续费文案 */
    isRenewPurchaseSuccessTip() {
      return this.purchaseRenewTip;
    },

    onPurchaseSuccessConfirm() {
      this.showPurchaseSuccessDialog = false;
    },

    /** 选套餐弹窗内打开实名（保持选套餐弹窗不关闭） */
    onPurchaseRealname() {
      this.openRealnameDialog();
    },

    /** 当前用户是否已实名（供模板绑定 verified） */
    isPurchaseVerified(realname) {
      return isRealnameVerified(realname);
    },
  },
};



// WEBPACK FOOTER //
// ./src/view/user/purchaseService/purchaseFlowMixin.js